/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  isEntityInvalid,
  newDirtyRow,
  PlainImmutablePageState,
  removeDirtyRow,
  setNestedEntityField,
  setNewDirtyType,
  setUpdatedDirtyType,
  whenExisting,
  whenNotExist,
} from '@gh/common-shared';
import { AutocompleteItem, getAutocompleteItemId, ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell, GridLayout } from '@gh/core-ui-grid';
import { firstByComparator, isNotNil, maybeMap, sortedIndexOfBy } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { first, flatMap, isEmpty, isNil, pick } from 'lodash';
import {
  getReportRohcAreaId,
  getReportRohcMasterCcId,
  getReportRohcOfficeAreaId,
  getReportRohcOfficeCcId,
  getReportRohcOfficeCcMasterId,
  getReportRohcOfficeId,
  queryReportRohcAllHousePlans,
  queryReportRohcAllOfficeCostCenters,
  queryReportRohcAllOffices,
  queryReportRohcAreaById,
  queryReportRohcAreas,
  queryReportRohcCurrentOfficeCcs,
  queryReportRohcCurrentOffices,
  queryReportRohcMasterCcById,
  queryReportRohcMasterCcs,
  REPORT_ROHC_DETAILS_INIT,
  ReportRohcArea,
  reportRohcAreaTable,
  ReportRohcDetails,
  ReportRohcDetailsTab, reportRohcAreaLayoutComparatorFactory,
  ReportRohcMasterCc,
  reportRohcMasterCcTable,
  ReportRohcOffice,
  ReportRohcOfficeCc,
  reportRohcOfficeCcTable,
  reportRohcOfficeTable,
  setReportRohcOfficeField, reportRohcMasterCcLayoutComparatorFactory,
} from '../../resources/report-roch-details.model';

@Injectable()
export class ReportRohcDetailsFormState extends PlainImmutablePageState<ReportRohcDetails> {
  selectedTab$ = this.select(({ selectedTab }) => selectedTab);
  areas$ = this.select(queryReportRohcAreas);
  currentOffices$ = this.select(queryReportRohcCurrentOffices);
  masterCostCenters$ = this.select(queryReportRohcMasterCcs);
  currentOfficeCostCenters$ = this.select(queryReportRohcCurrentOfficeCcs);
  selectedAreaId$ = this.select(({ selectedAreaId }) => selectedAreaId);
  officeFocusedCell$ = this.select(({ officeFocusedCell }) => officeFocusedCell);
  selectedMasterCcId$ = this.select(({ selectedMasterCcId }) => selectedMasterCcId);
  hasSelectedArea$ = this.selectedAreaId$.pipe(map$(isNotNil));
  hasSelectedMasterCc$ = this.selectedMasterCcId$.pipe(map$(isNotNil));

  constructor() {
    super(REPORT_ROHC_DETAILS_INIT, FormMode.EditMode);
  }

  get invalid(): boolean {
    return !isEmpty(this.invalidAreaOffices);
  }

  get invalidAreaOffices(): ReportRohcOffice[] {
    return flatMap(
      reportRohcAreaTable.byOrder(this.value.areas, 'name'),
      (area) => reportRohcOfficeTable.byGroup(this.value.areaOffices, 'area', getReportRohcAreaId(area))
        .filter(isEntityInvalid));
  }

  get selectedOffices(): ReportRohcOffice[] {
    return this.project(queryReportRohcAllOffices).filter(whenExisting);
  }

  get selectedAreaId(): Maybe<ID> {
    return this.value.selectedAreaId;
  }

  get selectedMasterCcId(): Maybe<ID> {
    return this.value.selectedMasterCcId;
  }

  get selectedOfficeCostCenters(): ReportRohcOfficeCc[] {
    return this.project(queryReportRohcAllOfficeCostCenters).filter(whenExisting);
  }

  get selectedHousePlans(): AutocompleteItem[] {
    return this.project(queryReportRohcAllHousePlans);
  }

  build(details: ReportRohcDetails): void {
    const areaLayout = this.value.layouts['area'];
    const masterCcLayout = this.value.layouts['masterCc'];

    // merge current layouts
    this.setValue({ ...details, selectedTab: this.value.selectedTab, layouts: this.value.layouts });
    this.resetDirty();

    this.setSelectedAreaId(maybeMap(
      firstByComparator(
        this.project(queryReportRohcAreas),
        reportRohcAreaLayoutComparatorFactory(areaLayout)),
      getReportRohcAreaId));
    this.setSelectedMasterCcId(maybeMap(
      firstByComparator(
        this.project(queryReportRohcMasterCcs),
        reportRohcMasterCcLayoutComparatorFactory(masterCcLayout)),
      getReportRohcMasterCcId));
  }

  selectTab(tab: ReportRohcDetailsTab): void {
    this.setField('selectedTab', tab);
  }

  setSelectedAreaId(id?: ID): void {
    this.setField('selectedAreaId', id);
  }

  setSelectedMasterCcId(id?: ID): void {
    this.setField('selectedMasterCcId', id);
  }

  setOfficeFocusedCell(focusedCell?: FocusedCell): void {
    this.setField('officeFocusedCell', focusedCell);
  }

  resetOfficeFocusedCell(): void {
    this.setOfficeFocusedCell();
  }

  addArea(area: ReportRohcArea): void {
    this.setFieldWithDirty(
      'areas',
      reportRohcAreaTable.insert(this.value.areas, setNewDirtyType(area)));
    this.syncAreaSelection();
  }

  updateArea(area: ReportRohcArea): void {
    this.setFieldWithDirty(
      'areas',
      reportRohcAreaTable.update(this.value.areas, setUpdatedDirtyType(area)));
  }

  removeArea(area: ReportRohcArea): void {
    // remove all offices of removed area
    this.removeOffices(reportRohcOfficeTable.byGroup(this.value.areaOffices, 'area', area.id));
    // remove area
    this.setFieldWithDirty(
      'areas',
      reportRohcAreaTable.mergeById(this.value.areas, getReportRohcAreaId(area), removeDirtyRow));
    this.syncAreaSelection();
  }

  addOffices(offices: ReportRohcOffice[]): void {
    this.markAreasAsUpdated(offices.map(getReportRohcOfficeAreaId));
    this.setFieldWithDirty('areaOffices', reportRohcOfficeTable.mergeAll(this.value.areaOffices, offices, newDirtyRow));
  }

  removeOffices(offices: ReportRohcOffice[]): void {
    // mark parent areas as dirty
    this.markAreasAsUpdated(offices.map(getReportRohcOfficeAreaId));
    // mark offices as deleted
    this.setFieldWithDirty(
      'areaOffices',
      reportRohcOfficeTable.mergeAllById(this.value.areaOffices, offices.map(getReportRohcOfficeId), removeDirtyRow));
    // remove all related office CCs
    const officeCcs = offices.reduce(
      (ccs, office) =>
        ccs.concat(reportRohcOfficeCcTable.byGroup(
          this.value.officeCostCenters,
          'office',
          getReportRohcOfficeId(office))),
      <ReportRohcOfficeCc[]>[]);
    this.removeOfficeCc(officeCcs);
  }

  setOfficeHousePlan(office: ReportRohcOffice, housePlan?: AutocompleteItem): void {
    this.startChangeDetection();
    this.setFieldWithDirty(
      'areaOffices',
      reportRohcOfficeTable.mergeById(
        this.value.areaOffices,
        getReportRohcOfficeId(office),
        (targetOffice) => {
          // set house plan only if it is changed
          if (maybeMap(targetOffice.housePlan, getAutocompleteItemId) !== maybeMap(housePlan, getAutocompleteItemId)) {
            return setReportRohcOfficeField(targetOffice, 'housePlan', housePlan && pick(housePlan, 'id', 'name'));
          } else {
            return targetOffice;
          }
        }));
    if (this.changed) {
      // mark parent area as dirty
      this.markAreasAsUpdated([getReportRohcOfficeAreaId(office)]);
      // remove all related office CCs
      this.removeOfficeCc(reportRohcOfficeCcTable.byGroup(
        this.value.officeCostCenters,
        'office',
        getReportRohcOfficeId(office)));
    }
  }

  addMasterCc(masterCc: ReportRohcMasterCc): void {
    this.setFieldWithDirty(
      'masterCostCenters',
      reportRohcMasterCcTable.insert(this.value.masterCostCenters, setNewDirtyType(masterCc)));
    this.syncMasterCcSelection();
  }

  updateMasterCc(masterCc: ReportRohcMasterCc): void {
    this.setFieldWithDirty(
      'masterCostCenters',
      reportRohcMasterCcTable.update(this.value.masterCostCenters, setUpdatedDirtyType(masterCc)));
  }

  removeMasterCc(masterCc: ReportRohcMasterCc): void {
    // remove all office ccs of removed master cc
    this.removeOfficeCc(reportRohcOfficeCcTable.byGroup(this.value.officeCostCenters, 'master', masterCc.id));
    // remove master cc
    this.setFieldWithDirty(
      'masterCostCenters',
      reportRohcMasterCcTable.mergeById(
        this.value.masterCostCenters,
        getReportRohcMasterCcId(masterCc),
        removeDirtyRow));
    this.syncMasterCcSelection();
  }

  addOfficeCcs(offices: ReportRohcOfficeCc[]): void {
    // mark parent master CC as updated
    this.markMasterCcsAsUpdated(offices.map(getReportRohcOfficeCcMasterId));
    // remove all requested office CC
    this.setFieldWithDirty(
      'officeCostCenters',
      reportRohcOfficeCcTable.mergeAll(this.value.officeCostCenters, offices, newDirtyRow));
  }

  removeOfficeCc(offices: ReportRohcOfficeCc[]): void {
    // mark parent master CC as updated
    this.markMasterCcsAsUpdated(offices.map(getReportRohcOfficeCcMasterId));
    // remove all requested office CC
    this.setFieldWithDirty(
      'officeCostCenters',
      reportRohcOfficeCcTable.mergeAllById(
        this.value.officeCostCenters,
        offices.map(getReportRohcOfficeCcId),
        removeDirtyRow));
  }

  setGridLayout(name: string, layout: GridLayout): void {
    this.setValue(setNestedEntityField(this.value, ['layouts', name], layout));
  }

  isCodeUnique(code: number): boolean {
// @ts-expect-error Legacy typing suppression
    return sortedIndexOfBy(reportRohcMasterCcTable.byOrder(this.value.masterCostCenters, 'code'), { code }, 'code') < 0;
  }

  private syncAreaSelection(): void {
    if (isNil(this.value.selectedAreaId)) {
      // if nothing is selected => select first area
      this.setSelectedAreaId(maybeMap(first(this.project(queryReportRohcAreas)), getReportRohcAreaId));
    } else if (whenNotExist(this.project(queryReportRohcAreaById, this.value.selectedAreaId))) {
      // if selected area was removed => reset selection
      this.setSelectedAreaId();
    }
  }

  private syncMasterCcSelection(): void {
    if (isNil(this.value.selectedMasterCcId)) {
      // if nothing is selected => select first area
      this.setSelectedMasterCcId(maybeMap(first(this.project(queryReportRohcMasterCcs)), getReportRohcMasterCcId));
    } else if (whenNotExist(this.project(queryReportRohcMasterCcById, this.value.selectedMasterCcId))) {
      // if selected area was removed => reset selection
      this.setSelectedMasterCcId();
    }
  }

  private markAreasAsUpdated(areaIds: ID[]): void {
    this.setFieldWithDirty(
      'areas',
      reportRohcAreaTable.mergeAllById(this.value.areas, areaIds, setUpdatedDirtyType));
  }

  private markMasterCcsAsUpdated(masterCcIds: ID[]): void {
    this.setFieldWithDirty(
      'masterCostCenters',
      reportRohcMasterCcTable.mergeAllById(this.value.masterCostCenters, masterCcIds, setUpdatedDirtyType));
  }
}
